import { useVirtualizer } from '@tanstack/react-virtual'
import FbButton from 'components/FbUI/FbButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import Checkbox from 'components/Checkbox/checkbox'
import { useEffect, useMemo, useRef, useState } from 'react'
import { BsXCircleFill } from 'react-icons/bs'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import { useDebounce } from 'use-debounce'
// Form import removed - using custom FilterSearchInput component
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { FilterStore } from '../FilterStore'

export interface SelectFilterOption {
  value: string
  label: string
}

export interface SelectFilterPaginationProps {
  onLoadMore: () => void
  hasMore: boolean
  isLoadingMore: boolean
  onSearchChange: (search: string) => void
  totalCount?: number
}

export interface SelectFilterProps {
  filterKey: string
  title: string
  options?: SelectFilterOption[]
  filterStore: FilterStore
  hideSearch?: boolean
  pagination?: SelectFilterPaginationProps
}

export function SelectFilter(props: SelectFilterProps) {
  const {
    pendingValueFilters: pending,
    setPendingValueFilters: setPending,
    clearPendingValueFilter: clearPending,
    registerFilterTitle,
  } = props.filterStore()
  const filter = pending[props.filterKey] ?? []
  const isActive = !!filter.length
  const isPaginated = !!props.pagination

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [internalFilters, setInternalFilters] = useState(filter ?? [])

  // Debounced search for server-side filtering in paginated mode
  const [debouncedSearch] = useDebounce(search, 300)

  // Trigger server-side search when debounced search changes in paginated mode
  useEffect(() => {
    if (isPaginated && props.pagination?.onSearchChange) {
      props.pagination.onSearchChange(debouncedSearch)
    }
  }, [debouncedSearch, isPaginated])

  // For paginated mode, we don't filter client-side (server does it)
  // We still need to sort selected items to the top
  const filteredOptions = useMemo(() => {
    if (!props.options) return undefined

    let result: SelectFilterOption[]
    if (!isPaginated) {
      // Client-side filtering only for non-paginated mode
      // .filter() creates a new array, so sorting is safe
      result = props.options.filter((o) => {
        const regex = new RegExp(search, 'gi')
        const labelTest = regex.test(o.label)
        const isSelected = internalFilters.some((s) => s?.value === o?.value)
        return isSelected || labelTest
      })
    } else {
      // In paginated mode, create a copy to avoid mutating props
      result = [...props.options]
    }

    // Sort selected items to the top
    return result.sort((a, b) => {
      const isASelected = internalFilters.some((s) => s?.value === a.value)
        ? 1
        : -1
      const isBSelected = internalFilters.some((s) => s?.value === b.value)
        ? 1
        : -1
      return isBSelected - isASelected
    })
  }, [props.options, search, internalFilters, isPaginated])

  const activeValueLabel = useMemo(() => {
    const length = filter?.length
    if (!length) return 'All'

    const firstActiveLabel = props.options?.find(
      (o) => o.value === filter[0]?.value
    )
    if (length === 1 && firstActiveLabel) {
      return firstActiveLabel.label
    }

    return `${length} selected`
  }, [filter])

  function handleInternalChange(option: SelectFilterOption, selected: boolean) {
    if (selected) {
      setInternalFilters((prev) =>
        prev.filter((f) => f?.value !== option.value)
      )
    } else {
      setInternalFilters((prev) => [...prev, option])
    }
  }

  function selectAll() {
    if (!props.options) return
    setInternalFilters(
      props.options.map((o) => ({
        value: o.value,
        label: o.label,
      }))
    )
  }

  function selectNone() {
    setInternalFilters([])
  }

  function clearFilters() {
    clearPending(props.filterKey)
  }

  function saveFilters() {
    setPending(
      props.filterKey,
      internalFilters.map((f) => ({
        value: f.value,
        label: f.label,
      }))
    )
  }

  // Syncs the internal filters with the filters in the context
  useEffect(() => {
    setInternalFilters(filter)
  }, [pending, open])

  useEffect(() => {
    registerFilterTitle(props.filterKey, props.title)
  }, [])

  function handleOpenChange(open: boolean) {
    if (!open) {
      // Apply filters when the dropdown closes
      saveFilters()
    }
    setOpen(open)
  }

  if (!props.options || !filteredOptions) return null

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <div className={'relative d-flex'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            title={props.title}
            onClick={() => setOpen(!open)}
            isActive={isActive}
            activeValueLabel={activeValueLabel}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton isActive={isActive} onClick={() => clearFilters()} />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        className={cn('bg-white w-[260px] lg:w-[235px] p-2')}
        sideOffset={5}
      >
        {!props.hideSearch && (
          <FilterSearchInput
            value={search}
            onChange={setSearch}
            isFocused={searchFocused}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
        )}

        <ItemsList
          options={filteredOptions}
          onSelect={handleInternalChange}
          selected={internalFilters}
          column={props.filterKey}
          pagination={props.pagination}
        />
        <hr className={'my-2'} />
        <div className={'w-full'}>
          {/* Hide All/None buttons in paginated mode since we don't have all items */}
          {!isPaginated && (
            <div className={'flex items-center justify-between gap-2'}>
              <FilterAllButton
                isSelected={internalFilters.length === props.options.length}
                onClick={() => selectAll()}
              />
              <FilterNoneButton
                isSelected={internalFilters.length === 0}
                onClick={() => selectNone()}
              />
            </div>
          )}
          {isPaginated && (
            <div className={'flex items-center justify-center'}>
              <FilterNoneButton
                isSelected={internalFilters.length === 0}
                onClick={() => selectNone()}
              />
            </div>
          )}

          <FbButton
            size={'sm'}
            className={'w-full mt-2'}
            data-testid={`select-filter-apply-${props.filterKey}`}
            onClick={() => {
              saveFilters()
              setOpen(false)
            }}
          >
            Apply
          </FbButton>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ItemsList(props: {
  options: SelectFilterOption[]
  selected: SelectFilterOption[]
  onSelect: (option: SelectFilterOption, selected: boolean) => void
  column: string
  pagination?: SelectFilterPaginationProps
}) {
  const parentRef = useRef<HTMLDivElement>(null)

  const virtual = useVirtualizer({
    count: props.options?.length ?? 1,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
  })

  // Handle scroll to detect when user is near bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!props.pagination) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50

    if (
      isNearBottom &&
      props.pagination.hasMore &&
      !props.pagination.isLoadingMore
    ) {
      props.pagination.onLoadMore()
    }
  }

  return (
    <div
      className={'mt-2 w-full'}
      ref={parentRef}
      onScroll={handleScroll}
      style={{
        height: `150px`,
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          height: `${virtual.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtual.getVirtualItems().map((item) => {
          const o = props.options[item.index]
          const isSelected = props.selected.some((s) => s?.value === o?.value)
          if (!o) return null
          return (
            <div
              key={props.column + item.index}
              className={cn(
                'max-w-full cursor-pointer flex items-center py-2 px-3 gap-2 hover:bg-gray-100 text-sm overflow-ellipsis whitespace-nowrap'
              )}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${item.size}px`,
                transform: `translateY(${item.start}px)`,
              }}
              // preventDefault stops the inner <label>'s activation behavior,
              // which would otherwise dispatch a synthetic click on the input
              // and bubble through this handler again — see FIR-7036.
              onClick={(e) => {
                e.preventDefault()
                props.onSelect(o, isSelected)
              }}
            >
              <Checkbox
                checked={isSelected}
                readOnly
                wrapperClassName="flex-shrink-0"
              />
              <div
                className={
                  'overflow-ellipsis overflow-hidden whitespace-nowrap'
                }
              >
                {o.label}
              </div>
            </div>
          )
        })}
      </div>
      {props.pagination?.isLoadingMore && (
        <div className="flex items-center justify-center py-2">
          <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  )
}

// Search input component
function FilterSearchInput({
  value,
  onChange,
  isFocused,
  onFocus,
  onBlur,
}: {
  value: string
  onChange: (value: string) => void
  isFocused: boolean
  onFocus: () => void
  onBlur: () => void
}) {
  const hasValue = value.length > 0

  const getContainerStyles = () => {
    if (isFocused) return searchContainerStyles.typing
    if (hasValue) return searchContainerStyles.filled
    return searchContainerStyles.default
  }

  const getIconColor = () => {
    if (isFocused) return 'text-brand' // #06512F
    if (hasValue) return 'text-fg-muted' // #C4C4C4
    return 'text-fg' // #191D23
  }

  return (
    <div className={cn('relative flex items-center', getContainerStyles())}>
      {/* Left icon - shown in default and filled states */}
      {!isFocused && (
        <PiMagnifyingGlass
          className={cn('w-5 h-5 flex-shrink-0', getIconColor())}
        />
      )}

      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder="Search"
        className={cn(
          'flex-1 bg-transparent border-none outline-none text-sm',
          'text-fg placeholder-fg-muted'
        )}
      />

      {/* Right icon - search icon when typing, clear button when filled */}
      {isFocused && (
        <PiMagnifyingGlass
          className={cn('w-5 h-5 flex-shrink-0', getIconColor())}
        />
      )}
      {!isFocused && hasValue && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="flex-shrink-0 p-0 mr-2 border-none bg-transparent cursor-pointer"
        >
          <BsXCircleFill className="w-[13px] h-[13px] text-fg-muted" />
        </button>
      )}
    </div>
  )
}

// Search container style definitions
const searchContainerStyles = {
  typing: cn(
    'bg-white',
    '!border',
    '!border-brand-active',
    'rounded-[6px]',
    'shadow-[0px_4px_8px_3px_rgba(6,81,47,0.1)]',
    'pl-2',
    'pr-3',
    'py-2',
    'gap-1'
  ),
  filled: cn(
    'bg-white',
    '!border',
    '!border-line',
    'rounded-[6px]',
    'pl-2',
    'pr-3',
    'py-2',
    'gap-1'
  ),
  default: cn(
    'bg-white',
    '!border',
    '!border-line',
    'rounded-[6px]',
    'pl-2',
    'pr-3',
    'py-2',
    'gap-1'
  ),
}

// Button style definitions
const filterButtonBase = cn(
  'flex-1',
  'h-8',
  'px-3',
  'text-sm',
  'font-medium',
  'rounded-md',
  'flex',
  'items-center',
  'justify-center',
  'transition-shadow'
)

const allButtonStyles = {
  selected: cn('!bg-surface-active', '!border', '!border-brand', '!text-brand'),
  deselected: cn(
    '!bg-new-company-pistachio-3',
    '!border',
    '!border-pistachio-100',
    '!text-fb-dark-green',
    'hover:shadow-[0px_1px_4px_rgba(12,12,13,0.05)]'
  ),
}

const noneButtonStyles = {
  selected: cn(
    '!bg-surface-raised',
    '!border',
    '!border-fg-secondary',
    '!text-fg'
  ),
  deselected: cn(
    '!bg-white',
    '!border',
    '!border-line',
    '!text-fg-secondary',
    'hover:shadow-[0px_1px_4px_rgba(12,12,13,0.05)]'
  ),
}

export function FilterAllButton({
  isSelected,
  onClick,
}: {
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={cn(
        filterButtonBase,
        isSelected ? allButtonStyles.selected : allButtonStyles.deselected
      )}
      onClick={onClick}
    >
      All
    </button>
  )
}

export function FilterNoneButton({
  isSelected,
  onClick,
}: {
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      className={cn(
        filterButtonBase,
        isSelected ? noneButtonStyles.selected : noneButtonStyles.deselected
      )}
      onClick={onClick}
    >
      None
    </button>
  )
}
